// src/components/ProcurementEstimateDetail.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import LoadingSpinner from '../../shared/LoadingSpinner';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Dropzone from '../../shared/Dropzone';
import PageLayout from '../../shared/PageLayout';
import DatePicker from '../../shared/DatePicker';
import Select from '../../shared/Select';
import { useNotification } from '../../shared/NotificationModal';
import { Table, THead, TBody, TR, TH, TD } from '../../shared/DataTable';
import Button from '../../shared/Button';
import { TEXT } from '../../shared/Typography';
import Alert from '../../shared/Alert';
import KpiCard from '../../shared/KpiCard';
import { QueueChip } from '../../shared/ApprovalQueue';
import { FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../shared/FormInput';

const API_BASE = import.meta.env.VITE_API_BASE || '';


function extractRoleNames(user) {
  const out = new Set();
  if (Array.isArray(user?._computedRoles))
    user._computedRoles.forEach(r => r && out.add(String(r).toLowerCase()));
  if (Array.isArray(user?.roles)) {
    user.roles.forEach(r => {
      if (!r) return;
      if (typeof r === 'string') out.add(r.toLowerCase());
      else if (typeof r?.name === 'string') out.add(r.name.toLowerCase());
    });
  }
  if (Array.isArray(user?.dbUser?.roles))
    user.dbUser.roles.forEach(r => r?.name && out.add(String(r.name).toLowerCase()));
  if (user?.role) out.add(String(user.role).toLowerCase());
  return Array.from(out);
}

async function openServerFile(filename, onError?: (msg: string) => void) {
  try {
    if (!filename) throw new Error('No filename');
    const url = `${API_BASE}/uploads/${encodeURIComponent(filename)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Unable to fetch file');
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    window.open(objUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
  } catch (e) {
    const msg = e?.message || 'Unable to open attachment';
    if (onError) onError(msg);
    else console.error(msg);
  }
}
async function downloadServerFile(filename, suggestedName = 'attachment', onError?: (msg: string) => void) {
  try {
    if (!filename) throw new Error('No filename');
    const url = `${API_BASE}/uploads/${encodeURIComponent(filename)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Unable to download file');
    const blob = await res.blob();
    const a = document.createElement('a');
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  } catch (e) {
    const msg = e?.message || 'Unable to download attachment';
    if (onError) onError(msg);
    else console.error(msg);
  }
}

interface Props {
  estimateId: any;
  user: any;
  onBack: any;
  onShowRequest: any;
}

export default function ProcurementEstimateDetail({ estimateId, user, onBack, onShowRequest }: Props) {
  const notification = useNotification();
  const [estimate, setEstimate] = useState<any>(null);
  const [original, setOriginal] = useState<any>(null);
  const [_history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // lookups
  const [clients, setClients] = useState<any[]>([]); // free-text allowed (list is optional)
  const [approvedVendors, setApprovedVendors] = useState<any[]>([]); // restrict to these
  const [jobCodes, setJobCodes] = useState<any[]>([]);

  // attachments
  const [busyUpload, setBusyUpload] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<any[]>([]);

  const printableRef = useRef(null);

  // debounce for CTR assignment
  const ctrDebounceRef = useRef(null);
  const lastCtrForClientRef = useRef('');

  function computeTotals(items, mode = 'line', ctrMarkupPercent = 0) {
    const rows = Array.isArray(items) ? items : [];
    let subtotal = 0;
    let markupBase = 0;

    for (const it of rows) {
      if (it?.type === 'header') continue;
      const qty = Number(it?.qty || 0);
      const rate = Number(it?.rate || 0);
      const base = qty * rate;
      subtotal += base;

      if (mode === 'line') {
        const pct = Number(it?.markupPct || 0);
        markupBase += base * (pct / 100);
      }
    }
    if (mode === 'ctr') markupBase = subtotal * (Number(ctrMarkupPercent || 0) / 100);
    const gstAmount = (subtotal + markupBase) * 0.1;
    const totalCost = subtotal + markupBase + gstAmount;
    const markupPct = subtotal ? (markupBase / subtotal) * 100 : 0;
    return {
      subtotal: Number(subtotal.toFixed(2)),
      gstAmount: Number(gstAmount.toFixed(2)),
      totalCost: Number(totalCost.toFixed(2)),
      markupPct: Number(markupPct.toFixed(2)),
    };
  }

  function normalize(e) {
    const safe = { ...(e || {}) };
    safe.estNumber = e?.estNumber || null;
    safe.clientName = String(e?.clientName || e?.client || '');
    safe.vendor = String(e?.vendor || e?.supplier || '');
    safe.site = String(e?.site || '');
    safe.activityName = String(e?.activityName || '');
    safe.startDate = e?.startDate ? new Date(e.startDate) : null;
    safe.endDate = e?.endDate ? new Date(e.endDate) : null;

    safe.clientRequestId = e?.clientRequestId || null;
    safe.procurementLink = e?.procurementLink || null;

    safe.workOrderNo = String(
      e?.workOrderNo || e?.project?.jobNo || e?.procurementLink?.workOrderNo || ''
    );
    const miapArr = Array.isArray(e?.miap)
      ? e.miap
      : Array.isArray(e?.procurementLink?.miap)
        ? e.procurementLink.miap
        : [];
    safe.miap = miapArr;

    safe.ctrNumber = String(e?.ctrNumber || '');

    safe.siteApprovalEmail = String(e?.siteApprovalEmail || '').toLowerCase();
    safe.clientApprovalEmail = String(e?.clientApprovalEmail || '').toLowerCase();

    safe.markupMode = e?.markupMode || 'line';
    safe.ctrMarkupPercent = Number(e?.ctrMarkupPercent || 0);

    safe.deliverables = String(e?.deliverables || '');
    safe.grpsInput = String(e?.grpsInput || '');

    safe.attachments = Array.isArray(e?.attachments) ? e.attachments : [];

    const items = Array.isArray(e?.items) ? e.items : [];
    safe.items = items.map((it, i) => {
      const qty = Number(it?.qty || it?.quantity || 0);
      const rate = Number(it?.rate || it?.unitRate || it?.unitPrice || 0);
      const uom = String(it?.uom || it?.unit || '');
      const desc = String(it?.description || '');
      const markupPct = typeof it?.markupPct === 'number' ? it.markupPct : Number(it?.markup || 0);
      const lineTotal = Number((qty * rate).toFixed(2));
      return {
        _id: it?._id || `line_${i}`,
        coo: String(it?.coo || ''),
        jobCode: String(it?.jobCode || ''),
        description: desc,
        qty,
        rate,
        uom,
        lineTotal,
        markupPct,
        section: String(it?.section || 'General'),
        type: String(it?.type || 'line'),
      };
    });

    const totals = computeTotals(safe.items, safe.markupMode, safe.ctrMarkupPercent);
    safe.subtotal = Number((e?.subtotal ?? totals.subtotal).toFixed(2));
    safe.gstAmount = Number((e?.gstAmount ?? totals.gstAmount).toFixed(2));
    safe.totalCost = Number((e?.totalCost ?? totals.totalCost).toFixed(2));
    safe.markupPct = Number((e?.markupPct ?? totals.markupPct).toFixed(2));

    safe.approvals = e?.approvals || {
      site: {
        email: safe.siteApprovalEmail,
        status: 'pending',
        decidedAt: null,
        decidedBy: '',
        note: '',
      },
      client: {
        email: safe.clientApprovalEmail,
        status: 'pending',
        decidedAt: null,
        decidedBy: '',
        note: '',
      },
    };
    safe.status = e?.status || 'draft';

    return safe;
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/estimates/${estimateId}`);
        if (cancelled) return;
        const e = normalize(res.data);
        setEstimate(e);
        setOriginal(e);
      } catch {
        if (!cancelled) setError('Failed to load estimate');
      } finally {
        if (!cancelled) setLoading(false);
      }
      try {
        const h = await axios.get(`/api/estimates/${estimateId}/history`);
        if (!cancelled) setHistory(Array.isArray(h.data) ? h.data : []);
      } catch { /* Ignore error */ }
        // intentionally empty
    }
    if (estimateId) load();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get('/api/clients');
        if (!cancel) setClients(Array.isArray(res.data) ? res.data : res.data?.items || []);
      } catch {
        if (!cancel) setClients([]);
      }
      try {
        const res = await axios.get('/api/vendors/approved');
        if (!cancel) setApprovedVendors(Array.isArray(res.data) ? res.data : res.data?.items || []);
      } catch {
        if (!cancel) setApprovedVendors([]);
      }
      try {
        const res = await axios.get('/api/jobcodes');
        if (!cancel) setJobCodes(Array.isArray(res.data) ? res.data : res.data?.items || []);
      } catch {
        if (!cancel) setJobCodes([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // permissions
  const roles = useMemo(() => extractRoleNames(user), [user]);
  const isAdmin = useMemo(
    () =>
      roles.some(
        r =>
          r === 'platform:admin' ||
          r === 'admin' ||
          r === 'procurement_request:*' ||
          r === 'estimate:admin'
      ),
    [roles]
  );
  const canEdit = isAdmin || roles.includes('jobs_estimates:requestor');

  const myEmail = String(user?.email || '').toLowerCase();
  const siteEmail = String(estimate?.siteApprovalEmail || '').toLowerCase();
  const clientEmail = String(estimate?.clientApprovalEmail || '').toLowerCase();
  const canSiteApprove = isAdmin || (!!siteEmail && myEmail === siteEmail);
  const canClientApprove = isAdmin || (!!clientEmail && myEmail === clientEmail);
  const canApprove = canSiteApprove || canClientApprove;

  // derive vendor names list from approvedVendors (before any early returns)
  const vendorNames = useMemo(
    () =>
      approvedVendors
        .map(v => (typeof v === 'string' ? v : v.name || v.vendor || ''))
        .filter(Boolean),
    [approvedVendors]
  );

  const status = String(estimate?.status || 'draft').toLowerCase();
  const isPending = status.includes('pending');
  const isApproved = status.includes('approved');
  const isRejected = status.includes('rejected');

  function _togglePreview() {
    setShowPreview(v => !v);
  }
  function toggleEdit() {
    if (canEdit) setEditMode(v => !v);
  }

  async function handleSave() {
    try {
      const payload = { ...estimate };
      delete payload.estNumber;
      payload.client = payload.clientName;
      payload.supplier = payload.vendor; // keep BE back-compat
      payload.miap = Array.isArray(payload.miap)
        ? payload.miap
        : String(payload.miap || '')
            .split(/[,\s;]+/)
            .map(s => s.trim())
            .filter(Boolean);

      // enforce approved vendor restriction
      const vendorNames = approvedVendors
        .map(v => (typeof v === 'string' ? v : v.name || v.vendor || ''))
        .filter(Boolean);
      if (payload.vendor && vendorNames.length && !vendorNames.includes(payload.vendor)) {
        notification.showWarning('Please select a vendor from the Approved Vendor list.', 'Validation Error');
        return;
      }

      await axios.put(`/api/estimates/${estimateId}`, payload);
      setOriginal(estimate);
      setEditMode(false);
    } catch {
      notification.showError('Error saving changes');
    }
  }
  function handleCancel() {
    setEstimate(original);
    setEditMode(false);
  }

  async function submitForApproval() {
    try {
      if (!estimate.siteApprovalEmail && !estimate.clientApprovalEmail) {
        notification.showWarning('Please set at least one approver email (site or client) before submitting.', 'Validation Error');
        return;
      }
      const { data } = await axios.post(
        `/api/estimates/${estimateId}/submit`
      );
      const e = normalize(data);
      setEstimate(e);
      setOriginal(e);
      notification.showSuccess('Submitted for approval. Approvers have been notified.');
    } catch (e) {
      notification.showError(e?.response?.data?.error || 'Failed to submit for approval');
    }
  }
  async function doApprove() {
    try {
      const r = await axios.post(
        `/api/estimates/${estimateId}/approve`
      );
      setEstimate(normalize(r.data));
      notification.showSuccess('Approved');
    } catch (e) {
      notification.showError(e?.response?.data?.error || 'Failed to approve');
    }
  }
  async function doReject() {
    try {
      const r = await axios.post(
        `/api/estimates/${estimateId}/reject`
      );
      setEstimate(normalize(r.data));
      notification.showSuccess('Rejected');
    } catch (e) {
      notification.showError(e?.response?.data?.error || 'Failed to reject');
    }
  }

  // ---- CTR auto-assign helpers ----
  async function ensureCtrForClient(name) {
    try {
      if (!name || estimate?.ctrNumber) return;
      if (lastCtrForClientRef.current === name) return;
      const { data } = await axios.post(
        `/api/estimates/${estimateId}/ensure-ctr`,
        { clientName: name }
      );
      lastCtrForClientRef.current = name;
      if (data?.ctrNumber) setEstimate(s => ({ ...s, ctrNumber: data.ctrNumber }));
    } catch {
      // silently ignore if BE endpoint not present
    }
  }
  function debounceEnsureCtr(name) {
    if (ctrDebounceRef.current) clearTimeout(ctrDebounceRef.current);
    ctrDebounceRef.current = setTimeout(() => ensureCtrForClient(name), 500);
  }

  // ----- attachments -----
  async function uploadAttachments(files) {
    if (!files?.length) return;
    setBusyUpload(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`/api/estimates/${estimateId}/attachments/upload`, fd);
      }
      const res = await axios.get(`/api/estimates/${estimateId}`);
      setEstimate(normalize(res.data));
      setPendingFiles([]);
    } catch (e) {
      notification.showError(e?.response?.data?.error || 'Failed to upload attachment(s)');
    } finally {
      setBusyUpload(false);
    }
  }
  async function deleteAttachment(att) {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await axios.delete(`/api/estimates/${estimateId}/attachments/${att._id}`);
      const res = await axios.get(`/api/estimates/${estimateId}`);
      setEstimate(normalize(res.data));
    } catch (e) {
      notification.showError(e?.response?.data?.error || 'Failed to delete attachment');
    }
  }

  async function downloadPdf() {
    try {
      const node = printableRef.current;
      if (!node) return;
      const canvas = await html2canvas(node, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pageWidth) / imgProps.width;

      let position = 0;
      let remainingHeight = imgHeight;
      while (remainingHeight > 0) {
        pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
        remainingHeight -= pageHeight;
        if (remainingHeight > 0) {
          pdf.addPage();
          position = 0;
        }
      }
      pdf.save(`Estimate_${estimate?.estNumber || estimateId}.pdf`);
    } catch (_e) {
      notification.showError('Failed to generate PDF');
    }
  }

  const totals = computeTotals(
    estimate?.items || [],
    estimate?.markupMode ?? 'line',
    estimate?.ctrMarkupPercent ?? 0
  );

  if (loading) return <LoadingSpinner message="Loading estimate..." fullScreen />;
  if (error) return <div className="p-4"><Alert variant="error" message={error} /></div>;
  if (!estimate) return <div className="p-4">Estimate not found.</div>;

  const subtitle = (
    <div className="flex items-center gap-2">
      <span>{estimate.estNumber || 'Unnumbered'}</span>
      {estimate.ctrNumber ? (
        <QueueChip>
          CTR: {estimate.ctrNumber}
        </QueueChip>
      ) : null}
      {estimate.procurementLink?.requestNumber ? (
        <QueueChip tone="blue">
          From PR: {estimate.procurementLink.requestNumber}
        </QueueChip>
      ) : null}
    </div>
  );

  const actions = (
    <>
      <Button variant="outline" size="sm" onClick={onBack}>
        ← Back
      </Button>
      {estimate?.procurementLink?.requestId && onShowRequest && (
        <Button variant="outline" size="sm" onClick={() => onShowRequest(estimate.procurementLink.requestId)} title="Open linked procurement request">
          Open Request
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={() => setShowPreview(v => !v)}>
        {showPreview ? 'Hide Preview' : 'Preview'}
      </Button>
      <Button variant="primary" size="sm" onClick={downloadPdf}>
        Download PDF
      </Button>

      {canEdit &&
        !isApproved &&
        !isRejected &&
        (editMode ? (
          <>
            <Button variant="success" size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={toggleEdit}>
            Edit
          </Button>
        ))}

      {canEdit && !isApproved && !isRejected && (
        <Button variant="primary" size="sm" onClick={submitForApproval} title="Send approval requests to site/client approvers">
          Submit for Approval
        </Button>
      )}

      {canApprove && isPending && (
        <>
          <Button variant="success" size="sm" onClick={doApprove}>
            Approve
          </Button>
          <Button variant="danger" size="sm" onClick={doReject}>
            Reject
          </Button>
        </>
      )}
    </>
  );

  return (
    <PageLayout title="Estimate Detail" subtitle={subtitle} actions={actions} showLogo={false}>
      <div className="space-y-6">

      {/* Preview */}
      {showPreview && (
        <>
          <div ref={printableRef} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <EstimatePrint estimate={estimate} totals={totals} />
          </div>
          <hr className="border-gray-100" />
        </>
      )}

      {/* Info form */}
      <div>
        <h3 className={`mb-3 ${TEXT.sectionTitle}`}>Info</h3>

        {/* Row 1: Client (free text + list), Vendor (restricted select), Site */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LabeledInput
            label="Client Name"
            value={estimate.clientName}
            onChange={v => {
              setEstimate({ ...estimate, clientName: v });
              if (!estimate.ctrNumber) debounceEnsureCtr(v);
            }}
            onBlur={() => ensureCtrForClient(estimate.clientName)}
            listId="clientList"
          />
          <datalist id="clientList">
            {clients.map((c, i) => {
              const name = typeof c === 'string' ? c : c.name || c.client || '';
              return <option key={i} value={name} />;
            })}
          </datalist>

          <Select
            label="Vendor (Approved)"
            value={estimate.vendor || ''}
            onChange={(val) => setEstimate({ ...estimate, vendor: val })}
            options={vendorNames.map(name => ({ value: name, label: name }))}
            placeholder={vendorNames.length ? 'Select a vendor...' : 'Loading vendors...'}
            disabled={!vendorNames.length}
          />

          <LabeledInput
            label="Site"
            value={estimate.site}
            onChange={v => setEstimate({ ...estimate, site: v })}
          />
        </div>

        {/* Row 2: Activity, Start/End on same line */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <LabeledInput
            label="Activity Name"
            value={estimate.activityName}
            onChange={v => setEstimate({ ...estimate, activityName: v })}
          />
          <DatePicker
            label="Start Date"
            value={toDateInput(estimate.startDate)}
            onChange={v => setEstimate({ ...estimate, startDate: v })}
          />
          <DatePicker
            label="End Date"
            value={toDateInput(estimate.endDate)}
            onChange={v => setEstimate({ ...estimate, endDate: v })}
          />
        </div>

        {/* Row 3: Work Order + MIAP + CTR (readonly) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <LabeledInput
            label="Work Order Number"
            value={estimate.workOrderNo}
            onChange={v => setEstimate({ ...estimate, workOrderNo: v })}
          />
          <LabeledInput
            label="MIAP (comma/space separated)"
            value={Array.isArray(estimate.miap) ? estimate.miap.join(', ') : estimate.miap || ''}
            onChange={v => setEstimate({ ...estimate, miap: v })}
          />
          <ReadOnly label="CTR Number" value={estimate.ctrNumber || '—'} />
        </div>

        {/* Row 4: GRPS Input & Deliverables */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <LabeledTextarea
            label="GRPS Input"
            value={estimate.grpsInput}
            onChange={v => setEstimate({ ...estimate, grpsInput: v })}
          />
          <LabeledTextarea
            label="Deliverables"
            value={estimate.deliverables}
            onChange={v => setEstimate({ ...estimate, deliverables: v })}
          />
        </div>

        {estimate.procurementLink?.requestNumber && (
          <div className={`mt-3 ${TEXT.body}`}>
            Linked Procurement Request: <b>{estimate.procurementLink.requestNumber}</b>
          </div>
        )}
      </div>

      <hr className="border-gray-100" />

      {/* Approvals */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className={TEXT.sectionTitle}>Approvals</h3>
          <div className="flex items-center gap-3">
            <StatusBadge status={estimate.status} />
            {(canSiteApprove || canClientApprove) && (
              <>
                {canSiteApprove && (
                  <QueueChip tone="green">
                    You can site-approve
                  </QueueChip>
                )}
                {canClientApprove && (
                  <QueueChip tone="blue">
                    You can client-approve
                  </QueueChip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LabeledInput
            label="Site Approval Email"
            value={estimate.siteApprovalEmail}
            onChange={v => setEstimate({ ...estimate, siteApprovalEmail: v })}
          />
          <LabeledInput
            label="Client Approval Email"
            value={estimate.clientApprovalEmail}
            onChange={v => setEstimate({ ...estimate, clientApprovalEmail: v })}
          />
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Items + Markup */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className={TEXT.sectionTitle}>Items</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => addManualLine()}>
              + Manual Line
            </Button>
            <div className="flex items-center gap-2">
              <label className={TEXT.label}>Markup Mode:</label>
              <Select
                variant="compact"
                value={estimate.markupMode}
                onChange={(val) => {
                  const markupMode = val;
                  const totals = computeTotals(
                    estimate.items,
                    markupMode,
                    estimate.ctrMarkupPercent
                  );
                  setEstimate(s => ({ ...s, markupMode, ...totals }));
                }}
                options={[
                  { value: 'line', label: 'Per-line' },
                  { value: 'ctr', label: 'CTR %' },
                ]}
              />
              {estimate.markupMode === 'ctr' && (
                <>
                  <label className={TEXT.label}>CTR %</label>
                  <input
                    className="border border-gray-300 rounded-md px-2 py-1 w-24"
                    type="number"
                    value={estimate.ctrMarkupPercent ?? 0}
                    onChange={(e: any) => {
                      const v = Number(e.target.value || 0);
                      const totals = computeTotals(estimate.items, 'ctr', v);
                      setEstimate(s => ({ ...s, ctrMarkupPercent: v, ...totals }));
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <Table density="compact" scroll={false} className="min-w-full bg-white border border-gray-200 rounded-lg">
              <THead>
                <TR>
                  <TH>COO</TH>
                  <TH>Job Code</TH>
                  <TH>Description</TH>
                  <TH align="right">Qty</TH>
                  <TH align="right">Rate</TH>
                  <TH>UoM</TH>
                  <TH align="right">Line Total</TH>
                  <TH align="right">Markup %</TH>
                  <TH></TH>
                </TR>
              </THead>
            <TBody>
              {(estimate.items || []).map((it, i) => (
                <TR key={it._id || i}>
                  <TD>
                    <input
                      className="w-28 border border-gray-300 rounded-md px-2 py-1"
                      value={it.coo || ''}
                      onChange={(e: any) => {
                        const coo = e.target.value;
                        setEstimate(s => {
                          const items = [...s.items];
                          items[i] = { ...items[i], coo };
                          return { ...s, items };
                        });
                      }}
                      placeholder="COO"
                    />
                  </TD>
                  <TD>
                    <input
                      className="w-36 border border-gray-300 rounded-md px-2 py-1"
                      list="jobCodeList"
                      value={it.jobCode || ''}
                      onChange={(e: any) => {
                        const jobCode = e.target.value;
                        setEstimate(s => {
                          const items = [...s.items];
                          items[i] = { ...items[i], jobCode };
                          return { ...s, items };
                        });
                      }}
                      placeholder="Start typing…"
                    />
                  </TD>
                  <TD>
                    <input
                      className="w-full border border-gray-300 rounded-md px-2 py-1"
                      value={it.description}
                      onChange={(e: any) => {
                        const v = e.target.value;
                        setEstimate(s => {
                          const items = [...s.items];
                          items[i] = { ...items[i], description: v };
                          return { ...s, items };
                        });
                      }}
                    />
                  </TD>
                  <TD align="right">
                    <input
                      type="number"
                      className="w-24 border border-gray-300 rounded-md px-2 py-1 text-right"
                      value={it.qty}
                      onChange={(e: any) => {
                        const qty = Number(e.target.value || 0);
                        setEstimate(s => {
                          const items = [...s.items];
                          const rate = Number(items[i].rate || 0);
                          items[i] = {
                            ...items[i],
                            qty,
                            lineTotal: Number((qty * rate).toFixed(2)),
                          };
                          const totals = computeTotals(items, s.markupMode, s.ctrMarkupPercent);
                          return { ...s, items, ...totals };
                        });
                      }}
                    />
                  </TD>
                  <TD align="right">
                    <input
                      type="number"
                      className="w-24 border border-gray-300 rounded-md px-2 py-1 text-right"
                      value={it.rate}
                      onChange={(e: any) => {
                        const rate = Number(e.target.value || 0);
                        setEstimate(s => {
                          const items = [...s.items];
                          const qty = Number(items[i].qty || 0);
                          items[i] = {
                            ...items[i],
                            rate,
                            lineTotal: Number((qty * rate).toFixed(2)),
                          };
                          const totals = computeTotals(items, s.markupMode, s.ctrMarkupPercent);
                          return { ...s, items, ...totals };
                        });
                      }}
                    />
                  </TD>
                  <TD>
                    <input
                      className="w-24 border border-gray-300 rounded-md px-2 py-1"
                      value={it.uom}
                      onChange={(e: any) => {
                        const uom = e.target.value;
                        setEstimate(s => {
                          const items = [...s.items];
                          items[i] = { ...items[i], uom };
                          return { ...s, items };
                        });
                      }}
                    />
                  </TD>
                  <TD align="right">
                    {it.lineTotal?.toFixed
                      ? it.lineTotal.toFixed(2)
                      : Number(it.lineTotal || 0).toFixed(2)}
                  </TD>
                  <TD align="right">
                    {estimate.markupMode === 'line' ? (
                      <input
                        type="number"
                        className="w-24 border border-gray-300 rounded-md px-2 py-1 text-right"
                        value={it.markupPct ?? 0}
                        onChange={(e: any) => {
                          const v = Number(e.target.value || 0);
                          setEstimate(s => {
                            const items = [...s.items];
                            items[i] = { ...items[i], markupPct: v };
                            const totals = computeTotals(items, s.markupMode, s.ctrMarkupPercent);
                            return { ...s, items, ...totals };
                          });
                        }}
                      />
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </TD>
                  <TD align="right">
                    <Button variant="linkDanger" onClick={() => {
                        setEstimate(s => {
                          const items = s.items.filter((_, idx) => idx !== i);
                          const totals = computeTotals(items, s.markupMode, s.ctrMarkupPercent);
                          return { ...s, items, ...totals };
                        });
                      }}>
                      Remove
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          </div>
        </div>
        <div>
          {/* Job code datalist */}
          <datalist id="jobCodeList">
            {jobCodes.map((j, idx) => (
              <option key={idx} value={typeof j === 'string' ? j : j.code || j.name || ''}>
                {typeof j === 'string' ? j : (j.name ? `${j.code || j.name} — ${j.name}` : (j.code || j.name || ''))}
              </option>
            ))}
          </datalist>
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Totals */}
      <div>
        <h3 className={`mb-3 ${TEXT.sectionTitle}`}>Totals</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KpiCard label="Subtotal" value={`$${(totals.subtotal || 0).toFixed(2)}`} />
          <KpiCard label="GST (10%)" value={`$${(totals.gstAmount || 0).toFixed(2)}`} />
          <KpiCard label="Markup %" value={`${(totals.markupPct || 0).toFixed(2)}%`} />
          <KpiCard label="Total" value={`$${(totals.totalCost || 0).toFixed(2)}`} />
        </div>
      </div>

      <hr className="border-gray-100" />

      {/* Attachments */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className={TEXT.sectionTitle}>Attachments</h3>
          <span className={TEXT.caption}>
            Includes approved quote(s) if created from a procurement request.
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
          <div className="md:col-span-2">
            <Dropzone
              multiple
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onFiles={files => setPendingFiles(prev => [...prev, ...files])}
              emptyText="Drag & drop files here, or click to browse"
              busy={busyUpload}
            />
          </div>
          <div>
            <Button variant="primary" size="sm" disabled={busyUpload || pendingFiles.length === 0} onClick={() => uploadAttachments(pendingFiles)}>
              Upload {pendingFiles.length ? `(${pendingFiles.length})` : ''}
            </Button>
            <Button variant="outline" size="sm" disabled={busyUpload || pendingFiles.length === 0} onClick={() => setPendingFiles([])} className="ml-2">
              Clear
            </Button>
            {pendingFiles.length > 0 && (
              <div className={`mt-2 ${TEXT.captionMuted}`}>
                Ready to upload:
                <ul className="list-disc ml-5">
                  {pendingFiles.map((f, i) => (
                    <li key={`${f.name}-${i}`}>
                      {f.name} ({Math.round(f.size / 1024)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 overflow-x-auto -mx-4 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <Table density="compact" scroll={false} className="min-w-full">
              <THead>
                <TR>
                  <TH>Label</TH>
                  <TH>Source</TH>
                  <TH>Filename</TH>
                  <TH align="right">Actions</TH>
                </TR>
              </THead>
            <TBody>
              {(estimate.attachments || []).map((a, idx) => (
                <TR key={a._id || a.filename || idx}>
                  <TD>{a.label || a.filename || '—'}</TD>
                  <TD>
                    {a.source === 'procurementQuote' ? (
                      <QueueChip tone="blue">
                        Quote from PR
                      </QueueChip>
                    ) : (
                      <QueueChip>
                        Manual
                      </QueueChip>
                    )}
                  </TD>
                  <TD>{a.filename || '—'}</TD>
                  <TD align="right">
                    <div className="flex justify-end gap-2">
                      {a.filename && (
                        <>
                          <Button variant="outline" size="xs" onClick={() => openServerFile(a.filename, (msg) => notification.showError(msg))}>
                            Open
                          </Button>
                          <Button variant="outline" size="xs" onClick={() => downloadServerFile(a.filename, a.label || a.filename, (msg) => notification.showError(msg))}>
                            Download
                          </Button>
                        </>
                      )}
                      {a.source !== 'procurementQuote' && (
                        <Button variant="dangerOutline" size="xs" onClick={() => deleteAttachment(a)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
              {(estimate.attachments || []).length === 0 && (
                <TR>
                  <TD align="center" colSpan={4} className="text-gray-500">
                    No attachments.
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
          </div>
        </div>
      </div>
      </div>
    </PageLayout>
  );

  function addManualLine() {
    const id = `m_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const newLine = {
      _id: id,
      coo: '',
      jobCode: '',
      description: '',
      qty: 1,
      rate: 0,
      uom: '',
      lineTotal: 0,
      markupPct: 0,
      section: 'General',
      type: 'line',
    };
    setEstimate(s => {
      const items = [...(s.items || []), newLine];
      const totals = computeTotals(items, s.markupMode, s.ctrMarkupPercent);
      return { ...s, items, ...totals };
    });
  }
}

function StatusBadge({ status }) {
  const s = String(status || '').toLowerCase();
  const tone = s.includes('approved')
    ? 'green'
    : s.includes('rejected')
      ? 'red'
      : 'amber';
  return <QueueChip tone={tone}>{status || 'Draft'}</QueueChip>;
}

function toDateInput(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function LabeledInput({ label, value, onChange, type = 'text', listId, onBlur }: { label: any; value: any; onChange: any; type?: string; listId?: any; onBlur?: any }) {
  return (
    <label className="block">
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <input
        type={type}
        className={FIELD_INPUT_CLASS}
        value={value ?? ''}
        onChange={(e: any) => onChange(e.target.value)}
        onBlur={onBlur}
        list={listId}
      />
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <textarea
        className={`${FIELD_INPUT_CLASS} resize-vertical`}
        value={value ?? ''}
        onChange={(e: any) => onChange(e.target.value)}
        rows={rows}
      />
    </label>
  );
}

function ReadOnly({ label, value }) {
  return (
    <label className="block">
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <div className="w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50 text-sm text-gray-700">{value ?? '—'}</div>
    </label>
  );
}

function EstimatePrint({ estimate, totals }) {
  const miapText = Array.isArray(estimate.miap) ? estimate.miap.join(', ') : estimate.miap || '';
  return (
    <div>
      <div className="text-center mb-3">
        <div className="text-xl font-semibold">Estimate</div>
        <div className={TEXT.bodyMuted}>{estimate.estNumber || ''}</div>
        {estimate.ctrNumber ? (
          <div className={TEXT.bodyMuted}>CTR: {estimate.ctrNumber}</div>
        ) : null}
        {estimate.procurementLink?.requestNumber && (
          <div className="text-sm text-blue-700">
            From Procurement Request: {estimate.procurementLink.requestNumber}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <strong>Client:</strong> {estimate.clientName || '-'}
        </div>
        <div>
          <strong>Vendor:</strong> {estimate.vendor || '-'}
        </div>
        <div>
          <strong>Site:</strong> {estimate.site || '-'}
        </div>
        <div>
          <strong>Activity:</strong> {estimate.activityName || '-'}
        </div>
        <div>
          <strong>Start:</strong>{' '}
          {estimate.startDate ? new Date(estimate.startDate).toLocaleDateString() : '-'}
        </div>
        <div>
          <strong>End:</strong>{' '}
          {estimate.endDate ? new Date(estimate.endDate).toLocaleDateString() : '-'}
        </div>
        <div>
          <strong>Work Order:</strong> {estimate.workOrderNo || '-'}
        </div>
        <div>
          <strong>MIAP:</strong> {miapText || '-'}
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 sm:mx-0 mb-3">
        <div className="inline-block min-w-full align-middle">
          <Table scroll={false} className="min-w-full">
            <THead>
              <TR>
                <TH>COO</TH>
                <TH>Job Code</TH>
                <TH>Description</TH>
                <TH align="right">Qty</TH>
                <TH align="right">Rate</TH>
                <TH>UoM</TH>
                <TH align="right">Total</TH>
              </TR>
            </THead>
          <TBody>
            {(estimate.items || []).map((it, i) => (
              <TR key={it._id || i}>
                <TD>{it.coo || ''}</TD>
                <TD>{it.jobCode || ''}</TD>
                <TD>{it.description}</TD>
                <TD align="right">{Number(it.qty || 0).toFixed(2)}</TD>
                <TD align="right">{Number(it.rate || 0).toFixed(2)}</TD>
                <TD>{it.uom}</TD>
                <TD align="right">{Number((it.qty || 0) * (it.rate || 0)).toFixed(2)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        </div>
      </div>

      <div className={`mb-2 ${TEXT.body}`}>
        <strong>Markup Mode:</strong>{' '}
        {estimate.markupMode === 'ctr'
          ? `CTR % (${Number(estimate.ctrMarkupPercent || 0)}%)`
          : 'Per-line'}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div></div>
        <div className="text-right">
          <div>Subtotal</div>
          <div>GST (10%)</div>
          <div className="font-semibold">Total</div>
        </div>
        <div className="text-right">
          <div>${(totals.subtotal || 0).toFixed(2)}</div>
          <div>${(totals.gstAmount || 0).toFixed(2)}</div>
          <div className="font-semibold">${(totals.totalCost || 0).toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}
